import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import {
  email,
  form,
  FormField,
  maxLength,
  minLength,
  pattern,
  required,
} from '@angular/forms/signals';
import { RouterLink } from '@angular/router';
import {
  AppSvgIconComponent,
  RequestState,
  SignalButton,
  SignalFormDirective,
  SignalTextField,
  State,
  validateSignalForm,
} from '@vallift/ngl';
import { environment } from '@environments/environment';
import { BaseComponent } from '@core/base/base-component';
import { WorkspaceAPIService } from '@core/api/workspace/services/workspace-api/workspaceAPI.service';
import { TokenStorageService } from '@core/services/token-storage.service';
import type {
  AvailablePlanResponse,
  GetAvailablePlansApiResponse,
  SignupLocalApiResponse,
  SignupLocalRequest,
} from '@core/api/workspace/models';
import {
  CreateCompanyRequestBillingCycle,
  CreateCompanyRequestPlanKey,
} from '@core/api/workspace/models';
import type { CreateCompanyRequestBillingCycle as BillingCycle } from '@core/api/workspace/models/createCompanyRequestBillingCycle';
import type { CreateCompanyRequestPlanKey as PlanKey } from '@core/api/workspace/models/createCompanyRequestPlanKey';
import { navigateToCreateCompany } from '@core/utils/auth-routing.util';

const PASSWORD_PATTERN = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).+$/;

const VALID_PLAN_KEYS = Object.values(CreateCompanyRequestPlanKey);
const VALID_BILLING_CYCLES = Object.values(CreateCompanyRequestBillingCycle);

interface SignupFormModel {
  name: string;
  companyName: string;
  email: string;
  password: string;
}

@Component({
  selector: 'app-signup-page',
  imports: [
    FormField,
    SignalFormDirective,
    RouterLink,
    SignalTextField,
    SignalButton,
    AppSvgIconComponent,
  ],
  templateUrl: './signup-page.html',
  styleUrl: './signup-page.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SignupPage extends BaseComponent implements OnInit {
  private readonly userAPIService = inject(WorkspaceAPIService);
  private readonly tokenStorage = inject(TokenStorageService);

  readonly signupState = new State<SignupLocalApiResponse>();
  readonly plansState = new State<GetAvailablePlansApiResponse>();
  readonly RequestState = RequestState;

  readonly selectedPlanKey = signal<PlanKey>(CreateCompanyRequestPlanKey.free);
  readonly selectedBillingCycle = signal<BillingCycle>(CreateCompanyRequestBillingCycle.MONTHLY);
  readonly returnUrl = signal<string | undefined>(undefined);

  readonly signupModel = signal<SignupFormModel>({
    name: '',
    companyName: '',
    email: '',
    password: '',
  });

  readonly signupForm = form(this.signupModel, (schemaPath) => {
    required(schemaPath.name, { message: 'This field is required' });
    maxLength(schemaPath.name, 100, { message: 'Maximum 100 characters allowed' });

    required(schemaPath.companyName, { message: 'This field is required' });
    minLength(schemaPath.companyName, 2, { message: 'Minimum 2 characters required' });
    maxLength(schemaPath.companyName, 100, { message: 'Maximum 100 characters allowed' });

    required(schemaPath.email, { message: 'This field is required' });
    email(schemaPath.email, { message: 'Enter a valid email address' });

    required(schemaPath.password, { message: 'This field is required' });
    minLength(schemaPath.password, 8, { message: 'Minimum 8 characters required' });
    maxLength(schemaPath.password, 128, { message: 'Maximum 128 characters allowed' });
    pattern(schemaPath.password, PASSWORD_PATTERN, {
      message: 'Password must include uppercase, lowercase, and a number',
    });
  });

  readonly selectedPlan = computed(() => {
    const plans = this.getPlans();
    return plans.find((plan) => plan.key === this.selectedPlanKey()) ?? null;
  });

  readonly customPricingLabel = 'Custom pricing';
  readonly currentYear = new Date().getFullYear();

  ngOnInit(): void {
    this.applyQueryParams();
    this.fetchPlans();
  }

  private applyQueryParams(): void {
    const planKeyParam = this.route.snapshot.queryParamMap
      .get('planKey')
      ?.trim()
      .toLowerCase();
    const billingCycleParam = this.route.snapshot.queryParamMap
      .get('billingCycle')
      ?.trim()
      .toUpperCase();
    const emailParam = this.route.snapshot.queryParamMap.get('email');
    const returnUrlParam = this.route.snapshot.queryParamMap.get('returnUrl');

    const planKey = VALID_PLAN_KEYS.includes(planKeyParam as PlanKey)
      ? (planKeyParam as PlanKey)
      : CreateCompanyRequestPlanKey.free;

    const billingCycle = VALID_BILLING_CYCLES.includes(billingCycleParam as BillingCycle)
      ? (billingCycleParam as BillingCycle)
      : CreateCompanyRequestBillingCycle.MONTHLY;

    this.selectedPlanKey.set(planKey);
    this.selectedBillingCycle.set(billingCycle);

    if (emailParam) {
      this.signupModel.update((model) => ({ ...model, email: emailParam }));
    }

    if (returnUrlParam?.startsWith('/') && !returnUrlParam.startsWith('//')) {
      this.returnUrl.set(returnUrlParam);
    }
  }

  private fetchPlans(): void {
    this.executeRequest({
      state: this.plansState,
      request: this.userAPIService.getAvailablePlans(),
      handleError: true,
      errorMessage: 'Failed to load plan details',
      onSuccess: () => {
        this.redirectIfPlanRequiresSales();
      },
    });
  }

  private redirectIfPlanRequiresSales(): void {
    const selectedPlan = this.getPlans().find((plan) => plan.key === this.selectedPlanKey());

    if (selectedPlan && !selectedPlan.canSignup) {
      this.router.navigate(['/contact-sales'], {
        queryParams: { planKey: selectedPlan.key },
      });
    }
  }

  getPlans(): AvailablePlanResponse[] {
    return this.plansState.response()?.data ?? [];
  }

  getPlanPrice(plan: AvailablePlanResponse): number | null {
    if (!plan.price) {
      return null;
    }
    return this.selectedBillingCycle() === CreateCompanyRequestBillingCycle.MONTHLY
      ? plan.price.monthlyPrice
      : plan.price.yearlyPrice;
  }

  hasPrice(plan: AvailablePlanResponse): boolean {
    return plan.price != null;
  }

  getBillingLabel(): string {
    return this.selectedBillingCycle() === CreateCompanyRequestBillingCycle.MONTHLY
      ? '/month'
      : '/year';
  }

  getCurrencySymbol(currency: string): string {
    const symbols: Record<string, string> = {
      USD: '$',
      INR: '₹',
    };
    return symbols[currency] || '$';
  }

  formatPrice(price: number): string {
    return price.toLocaleString();
  }

  getBillingCycleLabel(): string {
    return this.selectedBillingCycle() === CreateCompanyRequestBillingCycle.MONTHLY
      ? 'Monthly billing'
      : 'Yearly billing';
  }

  onGoogleSignInClicked(): void {
    const params = new URLSearchParams({
      purpose: 'SIGNUP',
      planKey: this.selectedPlanKey(),
      billingCycle: this.selectedBillingCycle(),
      returnUrl: this.returnUrl() ?? '/create-company',
    });
    window.location.href = `${environment.baseUrl}/auth/google?${params}`;
  }

  onSubmit(): void {
    if (!validateSignalForm(this.signupForm)) return;

    const { name, companyName, email, password } = this.signupModel();
    const payload: SignupLocalRequest = { name, email, password };

    this.executeRequest({
      state: this.signupState,
      request: this.userAPIService.signupLocal(payload),
      handleError: true,
      successMessage: 'Welcome to FormOps!',
      onSuccess: (response) => {
        this.tokenStorage.setSession(response.data);

        const returnUrl = this.returnUrl();
        if (returnUrl) {
          this.router.navigateByUrl(returnUrl);
          return;
        }

        void navigateToCreateCompany(this.router, {
          companyName: companyName.trim(),
          planKey: this.selectedPlanKey(),
          billingCycle: this.selectedBillingCycle(),
        });
      },
    });
  }
}
